import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import clsx from 'clsx'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import LinearProgress from '@material-ui/core/LinearProgress'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete'
import Avatar from '@material-ui/core/Avatar'
import Paper from '@material-ui/core/Paper'
import { getInitials } from 'lib/text-display'
import { useStudentListNameAgeStyles } from 'pages/pga-coach/students/student-list-name-age-styles'
import CloseIcon from 'components/material-symbol/icons/Close'
import AddIcon from 'components/material-symbol/icons/Add'
import CheckIcon from 'components/material-symbol/icons/Check'
import MessagingCheckbox from 'components/stream-messaging/messaging-checkbox'
import { useCoachStudents } from '../api-hooks'
import {
  AGE_GROUP_OPTIONS,
  NAME_MAX_LENGTH,
  RECENCY_OPTIONS,
  SKILL_LEVEL_OPTIONS,
} from '../constants'
import {
  applyRecency,
  overLimitMessage,
  pluralize,
  recencyValue,
  studentName,
} from '../format'
import useStyles from '../styles'
import ChoiceRows from './choice-rows'
import FormSection from './form-section'
import FilterListIcon from 'components/material-symbol/icons/FilterList'
import PersonIcon from 'components/material-symbol/icons/Person'

const AUDIENCE_MODE_OPTIONS = [
  {
    value: 'filters',
    title: 'Filter students',
    description: 'Choose by age, lessons, golf experience, and more.',
    icon: FilterListIcon,
  },
  {
    value: 'pick',
    title: 'Choose students',
    description: 'Search your students and add them one by one.',
    icon: PersonIcon,
  },
]

const SELECT_ALL_ID = 'select-all'

const filterStudentOptions = createFilterOptions({
  limit: 100,
  stringify: studentName,
})

// Matches the elevation of MUI Menu/Popover papers used across the app.
const StudentSearchPaper = props => <Paper elevation={8} {...props} />

const StudentName = ({ student, className, children }) => {
  const nameAgeClasses = useStudentListNameAgeStyles()

  return (
    <Box className={nameAgeClasses.nameCell}>
      <Avatar className={nameAgeClasses.studentAvatar} aria-hidden>
        {getInitials(studentName(student))}
      </Avatar>
      <Typography
        className={`${nameAgeClasses.text} ${className || ''}`}
        noWrap
      >
        {studentName(student)}
        {children}
      </Typography>
    </Box>
  )
}

StudentName.propTypes = {
  student: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
}

const optionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
})

const renderChips = (classes, getLabel) => (value, getTagProps) =>
  value.map((option, index) => (
    <Chip
      {...getTagProps({ index })}
      key={option.id}
      size="small"
      label={getLabel(option)}
      deleteIcon={<CloseIcon />}
      className={clsx(
        classes.skillChip,
        classes.skillChipSelected,
        classes.selectedChip,
      )}
    />
  ))

const IdFilter = ({ label, options, ids, onChange }) => {
  const classes = useStyles()

  if (options.length === 0) return null

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options}
      value={options.filter(option => ids.includes(option.id))}
      getOptionLabel={option => option.label}
      getOptionSelected={(option, value) => option.id === value.id}
      onChange={(_event, selected) => onChange(selected.map(o => o.id))}
      classes={{
        option: classes.filterOptionRow,
      }}
      PaperComponent={StudentSearchPaper}
      renderTags={renderChips(classes, option => option.label)}
      renderOption={(option, { selected }) => (
        <>
          <MessagingCheckbox checked={selected} tabIndex={-1} />
          <Typography className={classes.optionLabel}>
            {option.label}
          </Typography>
        </>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={ids.length ? undefined : 'Any'}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
    />
  )
}

IdFilter.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionShape).isRequired,
  ids: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
}

const StudentPicker = ({
  pickedStudents,
  pickedStudentIds,
  maxRecipients,
  onChange,
  skip,
}) => {
  const classes = useStyles()
  const { data, loading } = useCoachStudents({ skip })
  const students = data?.coachStudents || []
  // Saved ids count too, so picks that haven't loaded as chips yet still show.
  const pickedIds = new Set([
    ...pickedStudents.map(student => student.id),
    ...pickedStudentIds,
  ])
  const allPicked =
    students.length > 0 && students.every(student => pickedIds.has(student.id))
  // A campaign can't go to more than maxRecipients, so don't offer to pick
  // everyone when that would put the coach straight over the limit.
  const tooManyToSelectAll =
    Boolean(maxRecipients) && students.length > maxRecipients

  // A leading row that toggles every student, so it is never filtered by
  // the search and never counted as a picked student.
  const selectAllOption = {
    id: SELECT_ALL_ID,
    selectAll: true,
    count: students.length,
    disabled: tooManyToSelectAll,
  }

  const filterOptions = (options, state) => {
    const matches = filterStudentOptions(
      options.filter(option => !option.selectAll),
      state,
    )
    return state.inputValue || options.length === 0
      ? matches
      : [selectAllOption, ...matches]
  }

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={students}
      value={pickedStudents}
      loading={loading}
      getOptionLabel={option =>
        option.selectAll ? 'Select all students' : studentName(option)
      }
      getOptionSelected={(option, value) => option.id === value.id}
      getOptionDisabled={option => Boolean(option.disabled)}
      filterOptions={filterOptions}
      onChange={(_event, picked) => {
        if (picked.some(option => option.selectAll)) {
          if (!tooManyToSelectAll) onChange(allPicked ? [] : students)
          return
        }
        onChange(picked)
      }}
      classes={{
        option: classes.studentOptionRow,
      }}
      PaperComponent={StudentSearchPaper}
      renderTags={renderChips(classes, studentName)}
      renderOption={(option, { selected }) =>
        option.selectAll ? (
          <>
            <MessagingCheckbox
              checked={allPicked}
              indeterminate={!allPicked && pickedIds.size > 0}
              disabled={option.disabled}
              tabIndex={-1}
            />
            <Typography className={classes.optionLabel}>
              {option.disabled
                ? `Select up to ${maxRecipients.toLocaleString()} students (${option.count.toLocaleString()} is too many for one campaign)`
                : `Select all ${option.count.toLocaleString()} students`}
            </Typography>
          </>
        ) : (
          <>
            <MessagingCheckbox checked={selected} tabIndex={-1} />
            <StudentName student={option} />
          </>
        )
      }
      noOptionsText="No students found"
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Search your students"
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Students',
          }}
        />
      )}
    />
  )
}

StudentPicker.propTypes = {
  pickedStudents: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired }),
  ).isRequired,
  pickedStudentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxRecipients: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  skip: PropTypes.bool,
}

export default function AudienceStep({
  campaign,
  options,
  audience,
  audienceLoading,
  audienceError,
  mode,
  pickedStudents,
  nameError,
  skipSearch,
  onChange,
  onModeChange,
  onPickStudents,
}) {
  const classes = useStyles()
  const filters = campaign.audienceFilters

  const setFilters = patch =>
    onChange({ audienceFilters: { ...filters, ...patch } })

  const toggleSkill = value =>
    setFilters({
      skillLevels: filters.skillLevels.includes(value)
        ? filters.skillLevels.filter(level => level !== value)
        : [...filters.skillLevels, value],
    })

  const toggleExcluded = studentId =>
    setFilters({
      excludedStudentIds: filters.excludedStudentIds.includes(studentId)
        ? filters.excludedStudentIds.filter(id => id !== studentId)
        : [...filters.excludedStudentIds, studentId],
    })

  const excludedCount = filters.excludedStudentIds.length
  const count = audience?.count ?? 0
  const maxRecipients = audience?.maxRecipients
  const overLimit = Boolean(maxRecipients) && count > maxRecipients
  const countSummary =
    audienceError ||
    (overLimit
      ? `${count.toLocaleString()} students match.`
      : count === 0
        ? 'No students will get this message.'
        : `${pluralize(count, 'student')} will get this message.`)

  return (
    <>
      <TextField
        id="campaign-name"
        variant="outlined"
        label="Campaign name"
        required
        value={campaign.name}
        onChange={event => onChange({ name: event.target.value })}
        inputProps={{ maxLength: NAME_MAX_LENGTH }}
        error={nameError}
        helperText={
          nameError
            ? 'Give your campaign a name'
            : 'Only you see this. For example, "Spring clinic openings".'
        }
        fullWidth
      />

      <FormSection
        title="Who gets it"
        titleId="campaign-audience-mode-title"
        description="Parents or guardians get the message for juniors."
      >
        <Box className={classes.sectionContent}>
          <ChoiceRows
            options={AUDIENCE_MODE_OPTIONS}
            value={mode}
            onChange={onModeChange}
            labelledBy="campaign-audience-mode-title"
          />

          {mode === 'pick' ? (
            <StudentPicker
              pickedStudents={pickedStudents}
              pickedStudentIds={filters.studentIds}
              maxRecipients={maxRecipients}
              skip={skipSearch}
              onChange={onPickStudents}
            />
          ) : (
            <>
              <Typography className={classes.helperText}>
                Students must match every filter you use. Leave a filter empty
                to skip it.
              </Typography>
              <TextField
                id="campaign-age-group"
                select
                variant="outlined"
                label="Age group"
                value={filters.ageGroup}
                onChange={event => setFilters({ ageGroup: event.target.value })}
                fullWidth
              >
                {AGE_GROUP_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id="campaign-lesson-recency"
                select
                variant="outlined"
                label="Last lesson with you"
                value={recencyValue(filters)}
                onChange={event =>
                  onChange({
                    audienceFilters: applyRecency(filters, event.target.value),
                  })
                }
                fullWidth
              >
                {RECENCY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <FormSection
                title="Golf experience"
                description="Matches the level in each student’s golfer profile. Students without a profile are left out when you pick a level."
              >
                <Box className={classes.chips}>
                  {SKILL_LEVEL_OPTIONS.map(option => {
                    const selected = filters.skillLevels.includes(option.value)
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        icon={selected ? <CheckIcon /> : <AddIcon />}
                        clickable
                        variant={selected ? 'default' : 'outlined'}
                        className={clsx(
                          classes.skillChip,
                          selected
                            ? classes.skillChipSelected
                            : classes.skillChipUnselected,
                        )}
                        onClick={() => toggleSkill(option.value)}
                        aria-pressed={selected}
                      />
                    )
                  })}
                </Box>
              </FormSection>

              <IdFilter
                label="Booked any of these booking types"
                options={options.lessonTypes}
                ids={filters.lessonTypeIds}
                onChange={lessonTypeIds => setFilters({ lessonTypeIds })}
              />
              <IdFilter
                label="Registered for any of these past group events"
                options={options.groupEvents}
                ids={filters.groupEventIds}
                onChange={groupEventIds => setFilters({ groupEventIds })}
              />
              <IdFilter
                label="In any of these Jr. League programs"
                options={options.programs}
                ids={filters.programIds}
                onChange={programIds => setFilters({ programIds })}
              />
              <IdFilter
                label="Booked at any of these locations"
                options={options.facilities}
                ids={filters.facilityIds}
                onChange={facilityIds => setFilters({ facilityIds })}
              />
            </>
          )}
        </Box>
      </FormSection>

      <FormSection
        title="Recipients"
        description={countSummary}
        descriptionTestId="audience-count"
      >
        <Box className={classes.listStack}>
          {count > 0 ? (
            <Typography className={classes.helperText}>
              {overLimit
                ? overLimitMessage(maxRecipients, mode)
                : 'Uncheck anyone you want to leave out.'}
            </Typography>
          ) : null}
          {audienceLoading ? <LinearProgress color="primary" /> : null}
          {audience?.students?.length ? (
            <Box className={classes.previewList}>
              {audience.students.map(student => {
                const excluded = filters.excludedStudentIds.includes(student.id)
                return (
                  <Box key={student.id} className={classes.studentRow}>
                    <FormControlLabel
                      className={classes.studentRowLabel}
                      control={
                        <MessagingCheckbox
                          checked={!excluded}
                          onChange={() => toggleExcluded(student.id)}
                        />
                      }
                      label={
                        <StudentName
                          student={student}
                          className={excluded ? classes.excluded : undefined}
                        >
                          {student.isMinor ? ' (junior)' : ''}
                        </StudentName>
                      }
                    />
                  </Box>
                )
              })}
            </Box>
          ) : audienceError ? null : (
            <Typography className={classes.helperText}>
              {audienceLoading
                ? 'Finding students…'
                : mode === 'pick'
                  ? 'Search above to add students.'
                  : 'No students match. Try fewer filters.'}
            </Typography>
          )}
          {count > (audience?.students?.length || 0) ? (
            <Typography className={classes.helperText}>
              Showing the first {audience.students.length} by name.
            </Typography>
          ) : null}
          {excludedCount > 0 ? (
            <Box display="flex" alignItems="center" gridGap={8}>
              <Typography className={classes.helperText}>
                {pluralize(excludedCount, 'student')} left out
              </Typography>
              <Button
                size="small"
                color="primary"
                onClick={() => setFilters({ excludedStudentIds: [] })}
              >
                Add Back
              </Button>
            </Box>
          ) : null}
        </Box>
      </FormSection>
    </>
  )
}

AudienceStep.propTypes = {
  campaign: PropTypes.shape({
    name: PropTypes.string.isRequired,
    audienceFilters: PropTypes.object.isRequired,
  }).isRequired,
  options: PropTypes.shape({
    lessonTypes: PropTypes.arrayOf(optionShape).isRequired,
    groupEvents: PropTypes.arrayOf(optionShape).isRequired,
    programs: PropTypes.arrayOf(optionShape).isRequired,
    facilities: PropTypes.arrayOf(optionShape).isRequired,
  }).isRequired,
  audience: PropTypes.shape({
    count: PropTypes.number.isRequired,
    maxRecipients: PropTypes.number.isRequired,
    students: PropTypes.array.isRequired,
  }),
  audienceLoading: PropTypes.bool,
  audienceError: PropTypes.string,
  mode: PropTypes.oneOf(['filters', 'pick']).isRequired,
  pickedStudents: PropTypes.array.isRequired,
  nameError: PropTypes.bool,
  skipSearch: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onPickStudents: PropTypes.func.isRequired,
}
